'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface CalendarEvent {
  id: string;
  title: string;
  date: string;
  category: string;
}

interface EventCalendarProps {
  events: CalendarEvent[];
}

const EventCalendar: React.FC<EventCalendarProps> = ({ events }) => {
  const router = useRouter();
  const [currentMonth, setCurrentMonth] = useState(new Date(2026, 0, 1));

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear(); 
    const month = date.getMonth(); 
    const firstDay = new Date(year, month, 1); 
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek };
  };

  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const getEventsForDay = (day: number) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => event.date.startsWith(dateStr));
  };

  const renderCalendarDays = () => {
    const days = [];
    const totalCells = Math.ceil((daysInMonth + startingDayOfWeek) / 7) * 7;

    for (let i = 0; i < totalCells; i++) {
      const dayNumber = i - startingDayOfWeek + 1;
      const isValidDay = dayNumber > 0 && dayNumber <= daysInMonth;
      const dayEvents = isValidDay ? getEventsForDay(dayNumber) : [];
      const hasEvents = dayEvents.length > 0;

      days.push(
        <div
          key={i}
          className={`min-h-20 p-2 border border-border ${
            isValidDay ? 'bg-card hover:bg-muted transition-colors duration-200' : 'bg-muted/30'
          } ${hasEvents ? 'cursor-pointer' : ''}`}
        >
          {isValidDay && (
            <>
              <div className={`text-sm font-semibold mb-1 ${hasEvents ? 'text-primary' : 'text-foreground'}`}>
                {dayNumber}
              </div>
              {hasEvents && (
                <div className="space-y-1">
                  {dayEvents.slice(0, 2).map((event) => (
                    <div
                      key={event.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        router.push(`/events/${event.id}`);
                      }}
                      className="text-xs px-2 py-1 rounded bg-accent/10 text-accent truncate hover:bg-accent/20 transition-colors"
                      title={event.title}
                    >
                      {event.title}
                    </div>
                  ))}
                  {dayEvents.length > 2 && (
                    <div className="text-xs text-muted-foreground">
                      +{dayEvents.length - 2} more
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return days;
  };

  return (
    <div className="bg-card rounded-lg p-6 shadow-subtle">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-heading font-bold text-foreground">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <div className="flex items-center space-x-2">
          <button
            onClick={previousMonth}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
            aria-label="Previous month"
          >
            <Icon name="ChevronLeftIcon" size={20} />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-200"
            aria-label="Next month"
          >
            <Icon name="ChevronRightIcon" size={20} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-0 border border-border rounded-lg overflow-hidden">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div
            key={day}
            className="bg-primary text-primary-foreground text-center py-3 text-sm font-semibold border-r border-border last:border-r-0"
          >
            {day}
          </div>
        ))}
        {renderCalendarDays()}
      </div>

      <div className="mt-4 flex items-center space-x-4 text-sm">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-accent"></div>
          <span className="text-muted-foreground">Events Scheduled</span>
        </div>
      </div>
    </div>
  );
};

export default EventCalendar;